/**
 * Marks on a job (The Quiet Toolbar, 10/10). Pure: each one is read off the
 * job's own state, so doing the work clears it — no notification row is
 * involved. They replace bell rows that said the same thing once and then
 * sat unread forever:
 *
 *   job.flagged                    → red   "Tech flagged: <reason>" — newest OPEN
 *                                          flag; clears on Handled or when the tech's
 *                                          WO submit answers it (migration 724)
 *   (fail protocol paused)         → red   "Water off · waiting on NTE"
 *   cannot_complete                → red   "Can't finish"
 *   purchase_order.receive_undone  → amber "Parts not in yet"
 *   helper_entry_review            → amber "Check helper results"  (lead tech)
 *   online_booking.new             → blue  "Booked online"
 *   protocol_repair_approved       → green "Clear to repair"
 *   test_input_flagged             → red   "Office flagged this report" (tech)
 *   (work-order customer, no WO)   → red   "No work order yet"
 *   jobs.collect_on_site_at        → amber "Collect payment before you leave" (card hold #2037)
 *
 * Ordered most urgent first; the Grid card shows the first one and the hover
 * card shows them all.
 */
import type { MarkKind } from "@/components/mark";

export interface JobMarkFacts {
    status: string;
    tech_flag_reason?: string | null;
    /** A work-order customer's job with no work order on it (2026-10-21). */
    wo_missing?: boolean | null;
    fail_protocol_paused?: boolean | null;
    fail_protocol_cleared?: boolean | null;
    parts_blocked?: boolean | null;
    from_online_booking?: boolean | null;
    helper_results_to_review?: number | null;
    assigned_to?: string | null;
    /** Card hold (#2037): the card wouldn't hold; the office chose Collect on site. */
    collect_on_site?: boolean | null;
}

export interface JobMark {
    kind: MarkKind;
    label: string;
    title: string;
}

/** The tech's flag reasons are stored as codes; staff read plain words. */
const TECH_FLAG_LABELS: Record<string, string> = {
    wrong_location: "Wrong location",
    wrong_devices: "Wrong devices listed",
    ivr_not_working: "IVR not working",
    work_order_info_incorrect: "Work order information incorrect",
    pdf_wont_load: "PDF won't load",
    other: "Other",
};

export function techFlagLabel(reason: string): string {
    return TECH_FLAG_LABELS[reason] ?? reason;
}

/** True for the red mark the office can close with Handled. */
export function isTechFlagMark(label: string): boolean {
    return label.startsWith("Tech flagged: ");
}

const DONE = new Set(["completed", "closed", "cancelled"]);
const NOT_YET_BOOKED = new Set(["draft", "awaiting_scheduling", "unqueued"]);

export function jobMarks(
    job: JobMarkFacts,
    opts: { audience: "office" | "tech"; meId?: string | null; reportFlagged?: boolean } = { audience: "office" },
): JobMark[] {
    const out: JobMark[] = [];
    const done = DONE.has(job.status);
    if (opts.reportFlagged) {
        out.push({
            kind: "blocked",
            label: "Office flagged this report",
            title: "The office needs something fixed on this report. Open it.",
        });
    }
    if (job.tech_flag_reason) {
        const reason = techFlagLabel(job.tech_flag_reason);
        out.push({
            kind: "blocked",
            label: opts.audience === "tech" ? "You flagged this" : `Tech flagged: ${reason}`,
            title:
                opts.audience === "tech"
                    ? reason
                    : `${reason}. When you've dealt with it, press Handled on the job page and this clears.`,
        });
    }
    const woMark = workOrderMissingMark(job, opts.audience);
    if (woMark) out.push(woMark);
    const payMark = collectOnSiteMark(job);
    if (payMark) out.push(payMark);
    if (job.fail_protocol_paused) {
        out.push({
            kind: opts.audience === "tech" ? "waiting" : "blocked",
            label: "Water off · waiting on NTE",
            title: "A device failed. Waiting on the customer's not-to-exceed approval.",
        });
    }
    if (job.status === "cannot_complete" && opts.audience === "office") {
        out.push({
            kind: "blocked",
            label: "Can't finish",
            title: "The tech couldn't finish. Decide what happens next.",
        });
    }
    if (
        !done &&
        (job.helper_results_to_review ?? 0) > 0 &&
        (opts.audience === "office" || job.assigned_to === opts.meId)
    ) {
        const n = job.helper_results_to_review ?? 0;
        out.push({
            kind: "waiting",
            label: "Check helper results",
            title: `${n} device${n === 1 ? "" : "s"} your helper entered still need${n === 1 ? "s" : ""} the lead's review.`,
        });
    }
    if (!done && job.parts_blocked) {
        out.push({
            kind: "waiting",
            label: "Parts not in yet",
            title: "A purchase order on this job isn't received yet.",
        });
    }
    if (job.from_online_booking && NOT_YET_BOOKED.has(job.status) && opts.audience === "office") {
        out.push({
            kind: "new",
            label: "Booked online",
            title: "A customer booked this online. Confirm and schedule it.",
        });
    }
    if (!done && job.fail_protocol_cleared) {
        out.push({ kind: "back", label: "Clear to repair", title: "The customer approved the repair." });
    }
    return out;
}

/**
 * "No work order yet" (2026-10-21). Shown on the Grid card, the job page and
 * the tech's job screen whether or not the Quiet Toolbar switch is on — a
 * tech who leaves without the work order signs nothing (Cintas #092678170).
 */
export function workOrderMissingMark(
    job: Pick<JobMarkFacts, "wo_missing" | "status">,
    audience: "office" | "tech" = "office",
): JobMark | null {
    if (!job.wo_missing || DONE.has(job.status)) return null;
    return {
        kind: "blocked",
        label: "No work order yet",
        title:
            audience === "tech"
                ? "No work order on this job yet. Call the office before you start."
                : "This customer sends work orders, and this job doesn't have one. Upload it.",
    };
}

/**
 * Card hold (#2037): the card wouldn't hold and the office chose Collect on
 * site. Shown to the tech whether or not the Quiet Toolbar switch is on, like
 * "No work order yet": leaving without payment is the expensive mistake.
 */
export function collectOnSiteMark(job: Pick<JobMarkFacts, "collect_on_site" | "status">): JobMark | null {
    if (!job.collect_on_site || DONE.has(job.status)) return null;
    return {
        kind: "waiting",
        label: "Collect payment before you leave",
        title: "The customer's card wouldn't hold. Take payment at the visit.",
    };
}
